import React from 'react';
import { Search, X, Check, ChevronUp, ChevronDown, Trash2, Clock, RotateCcw } from 'lucide-react';
import { formatLastActive } from './_utils';

/**
 * RecentSignupsCard — searchable / filterable users table with inline
 * role + tier toggles and a delete trigger. Stateless; parent supplies
 * the filtered list, filter state, cohort options, and handlers.
 * Extracted from Admin.jsx.
 */
const RecentSignupsCard = ({
  stats,
  filteredSignups,
  hasActiveFilters,
  searchQ, setSearchQ,
  filterCohort, setFilterCohort,
  filterRole, setFilterRole,
  cohortOptions,
  onClearFilters,
  onToggleRole,
  onChangeTier,
  onExpireTrial,
  onResetTrial,
  onOpenDelete,
}) => (
  <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div>
        <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Recent signups</div>
        {stats && (
          <div className="text-xs text-enf-text-muted mt-1">
            {hasActiveFilters
              ? `${filteredSignups.length} of ${stats.recent_signups.length} shown`
              : `${stats.recent_signups.length} most recent`}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-enf-text-muted absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text" placeholder="Search email or name" value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            className="pl-8 pr-3 py-1.5 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-xs w-56 focus:outline-none focus:border-enf-accent-fg"
            data-testid="signup-search"
          />
        </div>
        <select value={filterCohort} onChange={(e) => setFilterCohort(e.target.value)}
          className="px-2 py-1.5 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-xs focus:outline-none focus:border-enf-accent-fg"
          data-testid="signup-cohort-filter">
          <option value="all">All cohorts</option>
          {cohortOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
          className="px-2 py-1.5 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-xs focus:outline-none focus:border-enf-accent-fg"
          data-testid="signup-role-filter">
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
        </select>
        {hasActiveFilters && (
          <button onClick={onClearFilters} className="text-xs text-enf-role-info hover:text-enf-text-primary flex items-center gap-1" data-testid="signup-clear-filters">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
    </div>

    {!stats || filteredSignups.length === 0 ? (
      <div className="text-sm text-enf-text-muted py-6 text-center">
        {hasActiveFilters ? 'No users match those filters.' : 'Nothing yet.'}
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-enf-text-muted border-b border-enf-surface-border">
            <tr>
              <th className="text-left font-semibold py-2 pr-3">Email</th>
              <th className="text-left font-semibold py-2 pr-3">Name</th>
              <th className="text-left font-semibold py-2 pr-3">Cohort</th>
              <th className="text-left font-semibold py-2 pr-3">Joined</th>
              <th className="text-left font-semibold py-2 pr-3">Last active</th>
              <th className="text-left font-semibold py-2 pr-3">Beta terms</th>
              <th className="text-left font-semibold py-2 pr-3">Source</th>
              <th className="text-left font-semibold py-2 pr-3">Role</th>
              <th className="text-left font-semibold py-2 pr-3">Tier</th>
              <th className="text-right font-semibold py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filteredSignups.map((u) => (
              <tr key={u.id} className="border-b border-enf-surface-border/60" data-testid={`signup-row-${u.email}`}>
                <td className="py-3 pr-3 text-enf-text-primary">{u.email}</td>
                <td className="py-3 pr-3 text-enf-text-secondary">{u.name || '—'}</td>
                <td className="py-3 pr-3 text-enf-role-info capitalize">{u.cohort || 'untagged'}</td>
                <td className="py-3 pr-3 text-enf-text-muted">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="py-3 pr-3 text-enf-text-muted" data-testid={`last-active-${u.email}`}>{formatLastActive(u.last_active_at)}</td>
                <td className="py-3 pr-3" data-testid={`beta-terms-${u.email}`}>
                  {u.beta_terms_accepted_at ? (
                    <span className="inline-flex items-center gap-1 text-enf-role-info text-xs" title={u.beta_terms_accepted_at}>
                      <Check className="w-3 h-3" /> {new Date(u.beta_terms_accepted_at).toLocaleDateString()}
                    </span>
                  ) : (
                    <span className="text-enf-text-muted text-xs">—</span>
                  )}
                </td>
                <td className="py-3 pr-3" data-testid={`source-${u.email}`}>
                  {u.referred_from ? (
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-semibold bg-enf-accent/10 text-enf-accent-fg border border-enf-accent-fg/30">
                      {u.referred_from}
                    </span>
                  ) : (
                    <span className="text-enf-text-muted text-xs">direct</span>
                  )}
                </td>
                <td className="py-3 pr-3">
                  <button
                    onClick={() => onToggleRole(u)}
                    className={`group inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs uppercase tracking-widest font-bold border transition-colors ${
                      u.role === 'admin'
                        ? 'text-enf-accent-fg border-enf-accent-fg/40 bg-enf-accent/10 hover:bg-enf-accent hover:text-enf-text-primary hover:border-enf-accent-fg'
                        : 'text-enf-text-secondary border-enf-surface-border bg-enf-surface-mid hover:bg-enf-accent/15 hover:text-enf-accent-fg hover:border-enf-accent-fg/40'
                    }`}
                    title={u.role === 'admin' ? 'Demote to user' : 'Promote to admin'}
                    data-testid={`role-toggle-${u.email}`}
                  >
                    {u.role}
                    {u.role === 'admin'
                      ? <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      : <ChevronUp className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
                  </button>
                </td>
                <td className="py-3 pr-3">
                  <select
                    value={u.subscription_tier || 'egnite'}
                    onChange={(e) => onChangeTier(u, e.target.value)}
                    className="text-xs uppercase tracking-widest font-bold px-2 py-1 rounded border bg-enf-surface-mid text-enf-text-secondary border-enf-surface-border hover:border-enf-accent-fg/40 hover:text-enf-accent-fg focus:outline-none focus:border-enf-accent-fg cursor-pointer"
                    data-testid={`tier-select-${u.email}`}
                    title="Lifetime tier grant — Stripe-paid 'Enferno' is set automatically by the webhook"
                  >
                    <option value="egnite">Egnite</option>
                    <option value="founder">Founder</option>
                    <option value="hero">Hero</option>
                    <option value="comp">Comp</option>
                    {u.subscription_tier === 'enferno' && (
                      <option value="enferno" disabled>Enferno (Stripe-paid)</option>
                    )}
                  </select>
                </td>
                <td className="py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => onResetTrial(u)}
                      className="text-enf-text-muted hover:text-enf-role-info p-1 transition-colors"
                      title={`Reset ${u.email}'s 14-day trial + clear AI usage (QA tool)`}
                      data-testid={`user-reset-trial-${u.email}`}
                      aria-label={`Reset trial for ${u.email}`}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onExpireTrial(u)}
                      className="text-enf-text-muted hover:text-enf-accent-fg p-1 transition-colors"
                      title={`Expire ${u.email}'s 14-day signup trial (QA tool)`}
                      data-testid={`user-expire-trial-${u.email}`}
                      aria-label={`Expire trial for ${u.email}`}
                    >
                      <Clock className="w-4 h-4" />
                    </button>
                    <button onClick={() => onOpenDelete(u)} className="text-enf-text-muted hover:text-enf-role-warning p-1 transition-colors" title={`Delete ${u.email}`} data-testid={`user-delete-${u.email}`} aria-label={`Delete ${u.email}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default RecentSignupsCard;
